import React from 'react';
import {
  ClipboardCheck, Eye, ExternalLink, MessageSquare, PanelRight, Pencil, RotateCcw,
} from 'lucide-react';
import { Task, TaskStatus } from '../../types';
import { TaskMode } from '../../taskTypes/module';
import { TaskShellActions } from './taskShellTypes';

function TaskStatusChip({
  mode,
  canClaimReview,
}: {
  mode: TaskMode;
  canClaimReview?: boolean;
}) {
  const { text, tone } = (() => {
    if (mode === 'preview') {
      return { text: 'Preview', tone: 'bg-amber-50 text-amber-800 border-amber-200' };
    }
    if (canClaimReview) {
      return { text: 'Available to review', tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
    }
    return { text: 'You are reviewing this task', tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
  })();
  return (
    <span className={`inline-flex h-7 items-center rounded-md border px-2 text-xs font-medium ${tone}`}>
      {text}
    </span>
  );
}

function TaskLifecycleStatusPill({ status }: { status: TaskStatus }) {
  const { label, tone } = (() => {
    switch (status) {
      case 'available':
        return { label: 'Available', tone: 'bg-gray-100 text-gray-700 border-gray-300' };
      case 'claimed':
        return { label: 'Claimed', tone: 'bg-blue-50 text-blue-800 border-blue-200' };
      case 'in_progress':
        return { label: 'In progress', tone: 'bg-blue-50 text-blue-800 border-blue-200' };
      case 'in_review':
        return { label: 'In review', tone: 'bg-amber-50 text-amber-800 border-amber-200' };
      case 'changes_requested':
        return { label: 'Changes requested', tone: 'bg-orange-50 text-orange-800 border-orange-200' };
      case 'approved':
        return { label: 'Approved', tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
      case 'archived':
        return { label: 'Archived', tone: 'bg-gray-100 text-gray-500 border-gray-300' };
      default:
        return { label: status, tone: 'bg-gray-100 text-gray-700 border-gray-300' };
    }
  })();
  return (
    <span className={`inline-flex h-7 items-center rounded-md border px-2 text-xs font-medium ${tone}`}>
      {label}
    </span>
  );
}

function TaskInstructionsButton({ href }: { href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex h-7 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 text-xs font-medium text-gray-700 transition-colors hover:border-primary hover:text-primary"
    >
      <ExternalLink className="h-3.5 w-3.5" />
      Instructions
    </a>
  );
}

function TaskExampleButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-7 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 text-xs font-medium text-gray-700 transition-colors hover:border-primary hover:text-primary"
    >
      <Eye className="h-3.5 w-3.5" />
      Task Example
    </button>
  );
}

function TitleEditor({
  task,
  onRename,
}: {
  task: Task;
  onRename: (title: string) => Promise<void>;
}) {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState(task.title);
  const [saving, setSaving] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (editing) return;
    setValue(task.title);
  }, [task.id, task.title, editing]);

  React.useEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    el?.focus();
    el?.select();
  }, [editing]);

  const commit = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setValue(task.title);
      setEditing(false);
      return;
    }
    if (trimmed === task.title) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onRename(trimmed);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    const displayTitle = task.title || 'Untitled task';
    const isPlaceholder = !task.title;
    return (
      <div className="flex items-center gap-2">
        <h1
          className={`truncate text-lg font-semibold ${isPlaceholder ? 'italic text-gray-400' : 'text-gray-900'}`}
        >
          {displayTitle}
        </h1>
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label="Rename task"
          title="Rename task"
          className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.currentTarget as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
          setValue(task.title);
          setEditing(false);
        }
      }}
      disabled={saving}
      placeholder="Name this task…"
      aria-label="Task title"
      className="w-full rounded-md border border-gray-300 bg-white px-2 py-0.5 text-lg font-semibold text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
    />
  );
}

/** A side-rail panel's header button, derived from `panels/registry`. */
export interface PanelToggle {
  id: string;
  label: string;
  badge: number;
  open: boolean;
  onToggle: () => void;
}

// Header icons live here rather than in the registry so the registry stays a
// data table the shell can reason about without importing lucide.
const PANEL_TOGGLE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  comments: MessageSquare,
  verification: ClipboardCheck,
};

export interface TaskShellHeaderProps {
  mode: TaskMode;
  task: Task | null | undefined;
  title: string;
  description: string;
  taskHeaderRef: React.RefObject<HTMLDivElement | null>;
  bleed: boolean;
  embedded: boolean;
  stickyTopPx?: number;
  instructionsHref: string | null;
  canRename: boolean;
  actions?: TaskShellActions;
  isAdminViewer: boolean;
  canClaimReview?: boolean;
  showStatusChip: boolean;
  /** One entry per available side-rail panel, in registry order. */
  panelToggles?: PanelToggle[];
  busy?: boolean;
  pendingAction?: string | null;

  onOpenTaskExample: () => void;
  onOpenAbandon: () => void;
  onOpenReleaseReview: () => void;
}

export function TaskShellHeader({
  mode,
  task,
  title,
  description,
  taskHeaderRef,
  bleed,
  embedded,
  stickyTopPx = 64,
  instructionsHref,
  canRename,
  actions,
  isAdminViewer,
  canClaimReview,
  showStatusChip,
  panelToggles = [],
  busy,
  pendingAction,

  onOpenTaskExample,
  onOpenAbandon,
  onOpenReleaseReview,
}: TaskShellHeaderProps) {
  return (
    <div
      ref={taskHeaderRef}
      role="region"
      aria-label="Task header"
      style={embedded ? undefined : { top: stickyTopPx }}
      className={embedded ? 'relative rounded-t-lg border border-gray-200 bg-white' : 'sticky z-20 border-b border-gray-200 bg-white'}
    >
      <div
        role="group"
        aria-label="Task header content"
        className={bleed ? 'px-4 sm:px-6 lg:px-8 py-3' : 'mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-3'}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {canRename && task && actions?.onRenameTask ? (
              <TitleEditor task={task} onRename={actions.onRenameTask} />
            ) : (
              <h1 className="truncate text-lg font-semibold text-gray-900">{title}</h1>
            )}
            {description && <p className="mt-1 text-sm text-gray-600">{description}</p>}
            {task?.ownerName && (mode === 'review' || isAdminViewer) && (
              <p className="mt-1 text-xs text-gray-500">
                Writer: <span className="font-medium text-gray-700">{task.ownerName}</span>
              </p>
            )}
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {showStatusChip && <TaskStatusChip mode={mode} canClaimReview={canClaimReview} />}
            {instructionsHref && <TaskInstructionsButton href={instructionsHref} />}
            {mode !== 'preview' && <TaskExampleButton onClick={onOpenTaskExample} />}
            {mode === 'preview' && task && <TaskLifecycleStatusPill status={task.status} />}
            {panelToggles.map((panel) => {
              const Icon = PANEL_TOGGLE_ICONS[panel.id] ?? PanelRight;
              return (
                <button
                  key={panel.id}
                  type="button"
                  onClick={panel.onToggle}
                  aria-label={`${panel.open ? 'Hide' : 'Show'} ${panel.label.toLowerCase()}`}
                  aria-pressed={panel.open}
                  className={[
                    'inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors',
                    panel.open
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-primary hover:text-primary',
                  ].join(' ')}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {panel.label}
                  {panel.badge > 0 && (
                    <span className={[
                      'ml-1 inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none',
                      panel.open ? 'bg-primary text-white' : 'bg-amber-100 text-amber-800',
                    ].join(' ')}>
                      {panel.badge}
                    </span>
                  )}
                </button>
              );
            })}
            {mode === 'work' && actions?.onAbandon && task && (task.status === 'claimed' || task.status === 'in_progress') && (
              <button
                type="button"
                onClick={onOpenAbandon}
                disabled={busy || !!pendingAction}
                className="inline-flex h-7 items-center gap-1.5 rounded-md border border-red-200 bg-white px-2 text-xs font-medium text-red-700 transition-colors hover:border-red-300 hover:bg-red-50 disabled:opacity-50"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Release task
              </button>
            )}
            {mode === 'review' && actions?.onReleaseReview && task && task.status === 'in_review' && (
              <button
                type="button"
                onClick={onOpenReleaseReview}
                disabled={busy || !!pendingAction}
                className="inline-flex h-7 items-center gap-1.5 rounded-md border border-red-200 bg-white px-2 text-xs font-medium text-red-700 transition-colors hover:border-red-300 hover:bg-red-50 disabled:opacity-50"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Release review
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
